// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import "@cloudscape-design/global-styles/index.css";

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fetchAuthSession } from "@aws-amplify/auth";

import {
	Box,
	Button,
	ColumnLayout,
	Container,
	ContentLayout,
	Flashbar,
	Header,
	Select,
	SpaceBetween,
	Spinner,
	Table,
} from "@cloudscape-design/components";
import { SelectProps } from "@cloudscape-design/components/select";

import { ReportFormat, UsageRecord, UserUsageBreakdown } from "../../types/usageCostAllocation";
import { formatTimestamp } from "../../util/formatTimestamp";
import { exportReport } from "../../util/reportExporter";
import { getCurrentCostRates } from "../../util/costRateConfig";
import { useAdminUsageRecords } from "./hooks/useAdminUsageRecords";
import { useAwsAccountCost } from "./hooks/useAwsAccountCost";
import BillingPeriodSelector from "../usage/billingPeriodSelector";
import CostRateSettings from "./costRateSettings";

const features = require("../../features.json");

function getCurrentBillingPeriod(): string {
	const now = new Date();
	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function formatCurrency(value: number): string {
	return new Intl.NumberFormat(undefined, {
		style: "currency",
		currency: "USD",
	}).format(value);
}

function formatCostRate(value: number): string {
	if (value === 0) {
		return formatCurrency(0);
	}
	if (value < 0.01) {
		return new Intl.NumberFormat(undefined, {
			style: "currency",
			currency: "USD",
			minimumSignificantDigits: 2,
			maximumSignificantDigits: 6,
		}).format(value);
	}
	return formatCurrency(value);
}

const JOB_TYPE_OPTIONS: SelectProps.Option[] = [
	{ value: "", label: "All" },
	{ value: "translation", label: "Translation" },
	{ value: "quickText", label: "Quick Text" },
	{ value: "readable", label: "Readable" },
];

const REPORT_FORMAT_OPTIONS: SelectProps.Option[] = [
	{ value: "csv", label: "CSV" },
	{ value: "pdf", label: "PDF" },
	{ value: "json", label: "JSON" },
];

function downloadBlob(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.click();
	URL.revokeObjectURL(url);
}

function generateCsv(records: UsageRecord[]): string {
	const headers = [
		"userName",
		"metricValue",
		"costRateApplied",
		"estimatedCost",
		"timestamp",
		"billingPeriod",
	];
	const rows = records.map((r) =>
		[
			r.userName || r.userId,
			r.metricValue,
			r.costRateApplied,
			r.estimatedCost,
			r.timestamp,
			r.billingPeriod,
		].join(",")
	);
	return [headers.join(","), ...rows].join("\n");
}

function downloadCsv(csv: string, filename: string): void {
	const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.click();
	URL.revokeObjectURL(url);
}

export default function AdminUsageDashboard() {
	const { t } = useTranslation();
	const [billingPeriod, setBillingPeriod] = useState(getCurrentBillingPeriod());
	const [jobTypeFilter, setJobTypeFilter] = useState<string>("");
	const [reportFormat, setReportFormat] = useState<ReportFormat>("csv");
	const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

	useEffect(() => {
		async function checkAdmin() {
			try {
				const session = await fetchAuthSession();
				const groups =
					(session.tokens?.idToken?.payload?.["cognito:groups"] as string[]) ?? [];
				setIsAdmin(groups.includes("admin"));
			} catch {
				setIsAdmin(false);
			}
		}
		checkAdmin();
	}, []);

	const { records, userBreakdown, summary, loading } = useAdminUsageRecords(
		billingPeriod,
		jobTypeFilter || undefined
	);

	const {
		costAmount: awsCostAmount,
		loading: awsCostLoading,
		error: awsCostError,
	} = useAwsAccountCost(billingPeriod, features.awsAccountCost);

	const selectedJobType =
		JOB_TYPE_OPTIONS.find((o) => o.value === jobTypeFilter) ??
		JOB_TYPE_OPTIONS[0];

	const selectedReportFormat =
		REPORT_FORMAT_OPTIONS.find((o) => o.value === reportFormat) ??
		REPORT_FORMAT_OPTIONS[0];

	const handleExportCsv = () => {
		const csv = generateCsv(records);
		downloadCsv(csv, `usage-${billingPeriod}.csv`);
	};

	const handleGenerateReport = async () => {
		try {
			const costRates = await getCurrentCostRates();
			const reportData = {
				billingPeriod,
				summary,
				userBreakdown,
				records,
				costRates: [costRates],
				...(features.awsAccountCost && awsCostAmount != null
					? { awsAccountCost: { billingPeriod, costAmount: awsCostAmount, currency: "USD" } }
					: {}),
			};
			const blob = exportReport(reportData, reportFormat, features.costingEnabled);
			const ext = reportFormat === "pdf" ? "pdf" : reportFormat === "json" ? "json" : "csv";
			downloadBlob(blob, `usage-report-${billingPeriod}.${ext}`);
		} catch (err) {
			console.error("Failed to generate report", err);
		}
	};

	const hasRecords = records.length > 0;

	if (isAdmin === null) {
		return (
			<Box textAlign="center" margin={{ vertical: "l" }}>
				<Spinner size="large" />
			</Box>
		);
	}

	if (!isAdmin) {
		return (
			<ContentLayout
				header={<Header variant="h1">{t("admin_access_denied_title")}</Header>}
			>
				<Box textAlign="center" margin={{ vertical: "l" }}>
					{t("admin_access_denied_message")}
				</Box>
			</ContentLayout>
		);
	}

	return (
		<ContentLayout
			header={
				<Header variant="h1" description={t("admin_dashboard_description")}>
					{t("admin_dashboard_title")}
				</Header>
			}
		>
			<SpaceBetween size="l">
				<SpaceBetween size="m" direction="horizontal">
					<BillingPeriodSelector
						selectedPeriod={billingPeriod}
						onChange={setBillingPeriod}
					/>
					<Select
						selectedOption={selectedJobType}
						onChange={(e) =>
							setJobTypeFilter(e.detail.selectedOption.value ?? "")
						}
						options={JOB_TYPE_OPTIONS}
						data-testid="job-type-filter"
					/>
					<Button onClick={handleExportCsv} iconName="download">
						{t("admin_export_csv")}
					</Button>
					<Select
						selectedOption={selectedReportFormat}
						onChange={(e) =>
							setReportFormat(
								(e.detail.selectedOption.value as ReportFormat) ?? "csv"
							)
						}
						options={REPORT_FORMAT_OPTIONS}
						data-testid="report-format-select"
					/>
					<Button
						onClick={handleGenerateReport}
						iconName="download"
						disabled={!hasRecords}
						data-testid="generate-report-button"
					>
						{t("admin_generate_report")}
					</Button>
				</SpaceBetween>

				{loading ? (
					<Box textAlign="center" margin={{ vertical: "l" }}>
						<Spinner size="large" />
					</Box>
				) : (
					<>
						<Container
							header={<Header variant="h2">{t("usage_summary")}</Header>}
						>
							<ColumnLayout columns={features.costingEnabled ? 2 : 1} variant="text-grid">
								<div>
									<Box variant="awsui-key-label">
										{t("usage_total_billable_units")}
									</Box>
									<Box variant="awsui-value-large">
										{summary.totalCharacterCount}
									</Box>
								</div>
								{features.costingEnabled && (
									<div>
										<Box variant="awsui-key-label">
											{t("usage_total_estimated_cost")}
										</Box>
										<Box variant="awsui-value-large">
											{formatCurrency(summary.totalEstimatedCost)}
										</Box>
									</div>
								)}
							</ColumnLayout>
						</Container>

						{features.awsAccountCost && (
							<Container
								header={
									<Header variant="h2">
										{t("admin_aws_account_cost_title")}
									</Header>
								}
							>
								{awsCostLoading ? (
									<Box textAlign="center" margin={{ vertical: "s" }}>
										<Spinner />
									</Box>
								) : awsCostError ? (
									<Flashbar
										items={[
											{
												type: "error",
												content: t("admin_aws_account_cost_error"),
											},
										]}
									/>
								) : (
									<Box variant="awsui-value-large">
										{formatCurrency(awsCostAmount ?? 0)}
									</Box>
								)}
							</Container>
						)}

						<Table
							columnDefinitions={[
								{
									id: "userId",
									header: t("admin_col_user_id"),
									cell: (item: UserUsageBreakdown) => item.userName || item.userId,
								},
								{
									id: "totalCharacterCount",
									header: t("usage_total_billable_units"),
									cell: (item: UserUsageBreakdown) =>
										item.totalCharacterCount,
								},
								...(features.costingEnabled
									? [
										{
											id: "totalEstimatedCost",
											header: t("usage_total_estimated_cost"),
											cell: (item: UserUsageBreakdown) =>
												formatCurrency(item.totalEstimatedCost),
										},
									]
									: []),
							]}
							items={userBreakdown}
							trackBy="userId"
							loading={loading}
							loadingText={t("generic_loading")}
							stripedRows
							stickyHeader
							header={
								<Header counter={`(${userBreakdown.length})`}>
									{t("admin_user_breakdown_title")}
								</Header>
							}
							empty={
								<Box
									textAlign="center"
									color="inherit"
									margin={{ vertical: "xs" }}
								>
									<b>{t("usage_no_records")}</b>
								</Box>
							}
						/>

						<Table
							columnDefinitions={[
								{
									id: "userId",
									header: t("admin_col_user_id"),
									cell: (item: UsageRecord) => item.userName || item.userId,
								},
								{
									id: "metricValue",
									header: t("usage_col_kb"),
									cell: (item: UsageRecord) => item.metricValue,
								},
								...(features.costingEnabled
									? [
										{
											id: "costRateApplied",
											header: t("usage_col_cost_rate"),
											cell: (item: UsageRecord) =>
												formatCostRate(item.costRateApplied),
										},
										{
											id: "estimatedCost",
											header: t("usage_col_estimated_cost"),
											cell: (item: UsageRecord) =>
												formatCurrency(item.estimatedCost),
										},
									]
									: []),
								{
									id: "timestamp",
									header: t("usage_col_timestamp"),
									cell: (item: UsageRecord) =>
										formatTimestamp(item.timestamp),
								},
							]}
							items={records}
							trackBy="id"
							loading={loading}
							loadingText={t("generic_loading")}
							stripedRows
							stickyHeader
							header={
								<Header counter={`(${records.length})`}>
									{t("usage_records_title")}
								</Header>
							}
							empty={
								<Box
									textAlign="center"
									color="inherit"
									margin={{ vertical: "xs" }}
								>
									<b>{t("usage_no_records")}</b>
								</Box>
							}
						/>

						{features.costingEnabled && <CostRateSettings />}
					</>
				)}
			</SpaceBetween>
		</ContentLayout>
	);
}
